import React, { useEffect, useState } from 'react';
import SearchableSelect from '../common/SearchableSelect';
import { CostingService } from '../../services/costing.service';
import { AuthService } from '../../services/auth.service';
import { useDashboardAccess } from '../dashboard/useDashboardAccess';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

interface PickOrder { id: string; orderNumber: string; buyerPoNumber?: string | null; status: string }

interface Props {
    value: string;
    onChange: (id: string) => void;
    label?: string;
    /** Offer shipped / closed orders too (filters on the costing pages). */
    openOnly?: boolean;
    /** Costing pages: always show, no costing/merchandising check. */
    always?: boolean;
    required?: boolean;
    disabled?: boolean;
    helperText?: string;
    sx?: object;
    'data-help'?: string;
}

/**
 * Buyer-order picker for the costing lane. Renders NOTHING unless order costing is switched on for the institution AND
 * the Merchandising module is enabled, so the requisition and stock-out forms are unchanged for every other tenant. Any
 * failure of the enabled check (no buyer-order access, network) also hides it. Orders are searched on the server as the
 * user types (open orders only), never pre-loaded.
 */
const OrderPicker: React.FC<Props> = ({ value, onChange, label = 'Buyer order (optional)', openOnly = true, always = false, required, disabled, helperText, sx, 'data-help': dataHelp }) => {
    const { enabledModules } = useDashboardAccess();
    const merchandising = enabledModules.has('merchandising');
    // Both the costing check and the order list need BUYER_ORDER_VIEW; without it the picker stays hidden
    const canViewOrders = usePermission(PERMISSIONS.BUYER_ORDER_VIEW);
    const [costingOn, setCostingOn] = useState(always);
    const [selected, setSelected] = useState<PickOrder | null>(null);
    const [options, setOptions] = useState<PickOrder[]>([]);
    const [loading, setLoading] = useState(false);
    const [text, setText] = useState('');
    const institutionId = AuthService.getSelectedInstitution();

    useEffect(() => {
        let live = true;
        if (always) { setCostingOn(true); return () => { live = false; }; }
        if (!institutionId || !merchandising || !canViewOrders) { setCostingOn(false); return () => { live = false; }; }
        CostingService.isEnabled(institutionId)
            .then(on => { if (live) setCostingOn(on === true); })
            .catch(() => { if (live) setCostingOn(false); });
        return () => { live = false; };
    }, [institutionId, merchandising, always, canViewOrders]);

    useEffect(() => {
        if (!costingOn || !institutionId) return undefined;
        let live = true;
        setLoading(true);
        const t = setTimeout(() => {
            CostingService.pickOrders(institutionId, text, openOnly)
                .then(rows => { if (live) setOptions(rows ?? []); })
                .catch(() => { if (live) setOptions([]); })
                .finally(() => { if (live) setLoading(false); });
        }, 250);
        return () => { live = false; clearTimeout(t); };
    }, [costingOn, institutionId, text, openOnly]);

    if (!costingOn) return null;
    return (
        <SearchableSelect<PickOrder>
            options={options}
            value={value}
            selectedOption={selected && selected.id === value ? selected : undefined}
            onChange={id => { setSelected(options.find(o => o.id === id) ?? null); onChange(id); }}
            getOptionLabel={o => `${o.orderNumber}${o.buyerPoNumber ? ` (PO ${o.buyerPoNumber})` : ''}`}
            label={label}
            loading={loading}
            required={required}
            disabled={disabled}
            helperText={helperText}
            disableClientFilter
            onInputChange={setText}
            sx={sx}
            {...(dataHelp ? { ["data-help"]: dataHelp } : {})}
        />
    );
};

export default OrderPicker;
